/**
 * LocalizedCurrencyDisplay - Display currency with user's preferred format
 */

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useUserPreferences } from '../context';
import { useReportingBaseCurrency } from '../hooks/useReportingBaseCurrency';
import { LtrToken } from './common/LtrToken';

export type NumberFormatOverride = 'comma_dot' | 'dot_comma';

export interface LocalizedCurrencyDisplayProps {
  value: number | null | undefined;
  /**
   * ISO currency code the amount is denominated in. **Required** — pass the
   * entity's own `currency_code` (a PO, invoice, price-list line, FX
   * transaction). An empty string renders the plain number with no symbol
   * (used by create forms before a currency/bank is chosen).
   *
   * For a **currency-less aggregate/total** (dashboard KPI, report summary the
   * backend computes in the tenant's base currency), do NOT hand-type a code —
   * use {@link PreferredCurrencyDisplay}, which resolves the reporting base
   * currency the backend summed it in.
   */
  currency: string;
  /** Override the number format instead of using user preference */
  overrideNumberFormat?: NumberFormatOverride;
  showTooltip?: boolean;
  emptyText?: string;
  className?: string;
}

/**
 * Format currency with a specific number format
 */
function formatCurrencyWithOverride(
  value: number,
  currency: string,
  numberFormat: NumberFormatOverride
): string {
  const locale = numberFormat === 'comma_dot' ? 'en-US' : 'de-DE';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
}

export function LocalizedCurrencyDisplay({
  value,
  currency,
  overrideNumberFormat,
  showTooltip = false,
  emptyText = '-',
  className,
}: LocalizedCurrencyDisplayProps) {
  const { formatCurrency } = useUserPreferences();

  if (value === null || value === undefined) {
    return <span className={className}>{emptyText}</span>;
  }

  // Guard against empty/invalid currency codes (e.g. before a bank is selected
  // in a create form). Fall back to plain number formatting without a symbol.
  if (!currency) {
    const plain = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return <LtrToken className={className}>{plain}</LtrToken>;
  }

  const formattedCurrency = overrideNumberFormat
    ? formatCurrencyWithOverride(value, currency, overrideNumberFormat)
    : formatCurrency(value, currency);

  const content = <LtrToken className={className}>{formattedCurrency}</LtrToken>;

  if (showTooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent>
            <p>{`${value} ${currency}`}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
}

export type PreferredCurrencyDisplayProps = Omit<LocalizedCurrencyDisplayProps, 'currency'>;

/**
 * PreferredCurrencyDisplay - Show a **currency-less** monetary aggregate in the
 * **reporting base currency** — the currency the backend actually summed it in,
 * resolved via `useReportingBaseCurrency()` (active company base → flagged
 * accounting base → tenant default → 'USD', mirroring
 * `FxService.resolve_reporting_base_currency`).
 *
 * This is the one preferred path for dashboard KPIs, report totals, and other
 * cross-document sums the backend computes in the tenant's base currency. It
 * exists so call sites never hand-type a currency for such aggregates.
 *
 * It deliberately does NOT follow `user.preferred_currency`: these totals are
 * never converted at report time (ERP P13), so relabelling one with the viewer's
 * preferred symbol would misrepresent an unconverted number. (The name is
 * historical — it predates that distinction; the anchor is the reporting base.)
 *
 * Do NOT use this for amounts that carry their own currency (a PO, invoice,
 * price-list line, FX transaction) — those must render in their own
 * `currency_code` via `<LocalizedCurrencyDisplay currency={...} />`.
 */
export function PreferredCurrencyDisplay(props: PreferredCurrencyDisplayProps) {
  const currency = useReportingBaseCurrency();
  return <LocalizedCurrencyDisplay {...props} currency={currency} />;
}
